import { z } from 'zod';

// Version suffix allows safe data format changes without breaking existing user data
const STORAGE_KEY = 'events_snapshot_v1';

const eventSchema = z.object({
  system: z.object({
    id: z.string(),
    last_modified: z.string(),
  }),
});

const storageStateSchema = z.record(z.string(), z.string());

type Event = z.infer<typeof eventSchema>;
type StorageState = z.infer<typeof storageStateSchema>;

/**
 * Loads the previous events snapshot from localStorage
 * @returns StorageState object mapping event IDs to last_modified timestamps
 */
function loadEventsSnapshot(): StorageState {
  let previousState: StorageState = {};
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const validated = storageStateSchema.safeParse(parsed);
      if (validated.success) {
        previousState = validated.data;
      } else {
        console.warn('Invalid localStorage data format:', validated.error);
      }
    }
  } catch (error) {
    console.warn('Could not parse localStorage data:', error);
  }
  return previousState;
}

/**
 * Builds a snapshot from an events array
 * @param events - Array of event objects with system.id and system.last_modified
 * @returns StorageState object mapping event IDs to last_modified timestamps
 */
function buildEventsSnapshot(events: readonly Event[]): StorageState {
  const state: StorageState = {};
  for (const event of events) {
    const validated = eventSchema.safeParse(event);
    if (!validated.success) {
      console.warn('Invalid event format:', validated.error);
      continue;
    }
    const { id, last_modified } = validated.data.system;
    state[id] = last_modified;
  }
  return state;
}

/**
 * Saves a snapshot of the current events to localStorage
 * @param events - Array of event objects with system.id and system.last_modified
 */
export function saveEventsSnapshot(events: readonly Event[] | null | undefined): void {
  if (!Array.isArray(events)) {
    return;
  }

  const state = buildEventsSnapshot(events);

  try {
    const validated = storageStateSchema.safeParse(state);
    if (validated.success) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validated.data));
    } else {
      console.error('Invalid state format before saving:', validated.error);
    }
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

/**
 * Checks if there are new or modified events compared to the saved snapshot
 * @param events - Array of event objects with system.id and system.last_modified
 * @returns boolean indicating if there are new or modified events
 */
export function hasEventUpdates(events: readonly Event[] | null | undefined): boolean {
  if (!Array.isArray(events)) {
    return false;
  }

  const previousState = loadEventsSnapshot();
  const currentState = buildEventsSnapshot(events);

  // Check if any event is new or modified
  for (const [id, last_modified] of Object.entries(currentState)) {
    if (!previousState[id] || previousState[id] !== last_modified) {
      return true;
    }
  }

  return false;
}
